import { useLayoutEffect, useMemo, useState } from 'react';
import { InstrSurgeryIcon, type InstrSurgeryIconName } from '../components/InstrSurgeryIcon';
import './InstrSurgeryPage.css';
import './PortalSectionLayout.css';
import './PortalSectionInteractions.css';

type Mode = 'before' | 'after';

interface InstructionItem {
  id: string;
  title: string;
  text: string;
}

const instructionContent: Record<Mode, InstructionItem[]> = {
  before: [
    {
      id: 'Ernährung vor dem Eingriff',
      title: 'Ernährung vor dem Eingriff',
      text: 'Bei Eingriffen unter örtlicher Betäubung sollten Sie vorher normal, aber nicht zu schwer essen, um Kreislaufproblemen vorzubeugen. Sollte eine Vollnarkose oder Sedierung geplant sein, müssen Sie in der Regel 6 Stunden vor dem Eingriff nüchtern bleiben (nichts essen, nicht rauchen) und 2 Stunden vorher nichts mehr trinken.',
    },
    {
      id: 'Medikamente & Blutverdünner',
      title: 'Medikamente & Blutverdünner',
      text: 'Nehmen Sie Ihre regulären Medikamente wie gewohnt ein, es sei denn, die Zahnärztin oder der Zahnarzt hat Ihnen etwas anderes mitgeteilt. Wichtig: Medikamente mit blutverdünnender Wirkung (z.B. Aspirin, Marcumar) müssen eventuell in Absprache mit Ihrem Hausarzt pausiert werden.',
    },
    {
      id: 'Kleidung & Begleitperson',
      title: 'Kleidung & Begleitperson',
      text: 'Tragen Sie bequeme, lockere Kleidung. Verzichten Sie auf Make-up und Schmuck. Organisieren Sie bei grösseren Eingriffen unbedingt eine Begleitperson, da Sie danach nicht selbst aktiv am Strassenverkehr teilnehmen dürfen.',
    },
  ],
  after: [
    {
      id: 'cooling',
      title: 'Richtiges Kühlen',
      text: 'Um eine mögliche Schwellung zu reduzieren, kühlen Sie das Operationsgebiet in den ersten 24 Stunden von aussen intermittierend (20 Min. kühlen, 20 Min. Pause). Wenden Sie die Eisbeutel nie direkt auf der Haut an, sondern wickeln Sie diese in ein Tuch ein.',
    },
    {
      id: 'swelling',
      title: 'Schwellung & Blutung',
      text: 'Eine leichte Blutung am Tag des Eingriffs und eventuell auch danach ist normal und wirkt durch die Verdünnung mit Speichel oft stärker, als sie tatsächlich ist. In diesem Fall sollten Sie nicht spülen, sondern ein sauberes Stofftaschentuch oder einen sterilen Tupfer auf die Wunde legen und für 20 Min fest darauf beissen. Halten Sie den Kopf aufrecht, lagern Sie ihn nicht tief und schlafen Sie am besten mit erhöhtem Oberkörper, gegebenenfalls mit einem zweiten Kissen. Die Schwellung ist häufig am zweiten Tag am stärksten, und je nach Blutgerinnung können auch blaue Flecken entstehen, die sich bis zum Hals ausdehnen können.',
    },
    {
      id: 'medication',
      title: 'Medikamente',
      text: 'Bitte halten Sie sich an die von Ihrem Zahnarzt angegebenen Dosierungen.',
    },
    {
      id: 'hygiene',
      title: 'Mundhygiene & Spülen',
      text: 'Am Tag des Eingriffs den Mund nicht intensiv ausspülen, da dies Nachblutungen begünstigen kann. Ab dem nächsten Tag vorsichtig Zähne putzen (Wundgebiet aussparen) und ggf. eine ärztlich verordnete Mundspüllösung verwenden. Die Zähne sollten weiterhin gründlich gereinigt werden, um Zahnfleischentzündungen zu vermeiden.',
    },
    {
      id: 'food',
      title: 'Essen & Trinken',
      text: 'Essen Sie erst wieder, wenn die örtliche Betäubung nachgelassen hat, damit Sie sich nicht auf die Zunge oder in die Wange beissen. Vermeiden Sie heisse Getränke/Speisen und verzichten Sie in den ersten Tagen auf Milch und Milchprodukte.',
    },
    {
      id: 'stimulants',
      title: 'Reizstoffe',
      text: 'Es ist ratsam auf Alkohol und Nikotin in den ersten drei Tagen nach dem Eingriff zu verzichten. Diese können Komplikationen, Wundheilungsstörungen und Entzündungen verursachen.',
    },
    {
      id: 'sport',
      title: 'Sport',
      text: 'Vermeiden Sie körperliche Anstrengungen in den ersten drei Tagen nach der Operation. Meiden Sie zudem Wärme, wie beispielsweise heisse Bäder, Sauna oder intensives Sonnenbad.',
    },
  ],
};

const modeConfig = {
  before: {
    label: 'Vor dem Eingriff',
    subline: 'Präoperative und postoperative Verhaltensregeln.',
  },
  after: {
    label: 'Nach dem Eingriff',
    subline: 'Präoperative und postoperative Verhaltensregeln.',
  },
} satisfies Record<Mode, { label: string; subline: string }>;

const sectionIconNames: Record<string, InstrSurgeryIconName> = {
  cooling: 'snowflake',
  swelling: 'droplet',
  medication: 'capsules',
  hygiene: 'dental-care',
  food: 'cutlery',
  stimulants: 'no-smoking',
  sport: 'running',
};

const getSectionIconName = (itemId: string) => sectionIconNames[itemId] ?? 'medicine';

export function InstrSurgeryPage() {
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const [mode, setMode] = useState<Mode>('before');
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  const items = useMemo(() => instructionContent[mode], [mode]);

  const toggleMode = (nextMode: Mode) => {
    setMode(nextMode);
    setOpenItemId(null);
  };

  const toggleItem = (itemId: string) => {
    setOpenItemId((current) => (current === itemId ? null : itemId));
  };

  return (
    <div className="portal-section-page portal-section-page--advice instr-surgery-page portal-page-surface">
      <div className="instr-surgery-page__inner portal-page-content">
        <header className="instr-surgery-page__header portal-page-heading">
          <h1>Ratgeber und Tipps</h1>
          <p>{modeConfig[mode].subline}</p>
        </header>

        <section className="portal-section-card instr-surgery-page__hero" aria-labelledby="surgery-guide-title">
          <div className="instr-surgery-page__document-tile">
            <InstrSurgeryIcon name="document" />
          </div>
          <div className="instr-surgery-page__hero-copy">
            <h2 id="surgery-guide-title">Tipps bei chirurgischen Eingriffen</h2>
            <p>Wichtige Verhaltensregeln für einen optimalen Heilungsverlauf.</p>
          </div>
          <a
            className="instr-surgery-page__export"
            href="/patient-documents/Verhalten_chirurgischer_Eingriff_RiviDent.pdf"
            download
            aria-label={`Verhalten ${mode === 'before' ? 'vor' : 'nach'} chirurgischen Eingriffen als PDF herunterladen`}
          >
            <span>Als PDF exportieren</span>
            <InstrSurgeryIcon name="download" />
          </a>
        </section>

        <section className="instr-surgery-page__tabs" aria-label="Zeitpunkt des Eingriffs">
          <button
            type="button"
            onClick={() => toggleMode('before')}
            aria-pressed={mode === 'before'}
            className={`instr-surgery-page__tab${mode === 'before' ? ' instr-surgery-page__tab--active' : ''}`}
          >
            <InstrSurgeryIcon name="calendar" />
            <span>Vor dem Eingriff</span>
          </button>
          <button
            type="button"
            onClick={() => toggleMode('after')}
            aria-pressed={mode === 'after'}
            className={`instr-surgery-page__tab${mode === 'after' ? ' instr-surgery-page__tab--active' : ''}`}
          >
            <InstrSurgeryIcon name="medical-bag" />
            <span>Nach dem Eingriff</span>
          </button>
        </section>

        <section className="instr-surgery-page__accordion" aria-label={modeConfig[mode].label}>
          {items.map((item, index) => {
            const isOpen = openItemId === item.id;
            const triggerId = `surgery-${mode}-${index}-trigger`;
            const panelId = `surgery-${mode}-${index}-panel`;

            return (
              <article
                key={item.id}
                className={`portal-section-card portal-section-card--flush instr-surgery-page__accordion-card${isOpen ? ' instr-surgery-page__accordion-card--open' : ''}`}
              >
                <button
                  id={triggerId}
                  type="button"
                  onClick={() => toggleItem(item.id)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  className="instr-surgery-page__accordion-trigger"
                >
                  <span className="instr-surgery-page__section-tile">
                    {mode === 'before' ? (
                      <InstrSurgeryIcon name={index === 0 ? 'cutlery' : index === 1 ? 'capsules' : 'hanger'} />
                    ) : (
                      <InstrSurgeryIcon name={getSectionIconName(item.id)} className="h-6 w-6" />
                    )}
                  </span>

                  <span className="instr-surgery-page__section-title">{item.title}</span>

                  <svg
                    viewBox="0 0 24 24"
                    className={`instr-surgery-page__chevron${isOpen ? ' instr-surgery-page__chevron--open' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    aria-hidden="true"
                  >
                    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                {isOpen ? (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={triggerId}
                    className="instr-surgery-page__accordion-panel"
                  >
                    {item.text}
                  </div>
                ) : null}
              </article>
            );
          })}
        </section>

        <section className="portal-section-card instr-surgery-page__contact" aria-labelledby="surgery-contact-title">
          <div className="instr-surgery-page__support-tile">
            <InstrSurgeryIcon name="headset" />
          </div>
          <div className="instr-surgery-page__contact-copy">
            <h3 id="surgery-contact-title">Haben Sie weitere Fragen oder akute Beschwerden?</h3>
            <p>
              Rufen Sie uns gerne direkt an unter <a href="tel:0526254625">052 625 46 25</a>.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
